import React, { useEffect, useState } from "react";
import { Table, DatePicker, Select, Space, Card, Tabs } from "antd";
import moment from "moment";
import { formatDateTimeInTz } from "../../helper/timezone";
import { Link, useSearchParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";
import EmployeeCalendarTab from "./EmployeeCalendarTab";

const statusOptions = ["present", "late", "half_day", "wfh", "outside_geofence", "auto_logged_out", "absent"].map((s) => ({
  value: s,
  label: s.replace(/_/g, " "),
}));

const Attendance = () => {
  const { request } = useRequest();
  const [sessions, setSessions] = useState([]);
  const [date, setDate] = useState(moment());
  const [status, setStatus] = useState(undefined);
  // ?tab=calendar opens the Employee calendar tab directly (used by links and bookmarks)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "calendar" ? "calendar" : "daily";

  const fetchData = (d = date, s = status) => {
    const params = new URLSearchParams({ date: d.format("YYYY-MM-DD") });
    if (s) params.append("status", s);
    request({
      url: `${apiPath.attendanceDashboard}?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => setSessions(res?.data?.sessions || []),
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "Employee",
      dataIndex: ["employee", "name"],
      render: (v, record) => <Link to="/activity-history" state={{ employeeId: record.employee?._id, employeeName: v }}>{v}</Link>,
    },
    { title: "Employee ID", dataIndex: ["employee", "employeeId"] },
    { title: "Office", dataIndex: ["office", "name"] },
    { title: "Shift Start", dataIndex: "loginAt", render: (v, r) => formatDateTimeInTz(v, r.office?.timezone) },
    { title: "Shift End", dataIndex: "logoutAt", render: (v, r) => formatDateTimeInTz(v, r.office?.timezone) },
    { title: "Ended By", dataIndex: "logoutType" },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    {
      title: "Month",
      render: (_, record) => (
        <Link to="/attendance?tab=calendar" state={{ employeeId: record.employee?._id }}>
          View calendar
        </Link>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Attendance" subtitle="Daily attendance, and each employee's month at a glance" />
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setSearchParams(k === "calendar" ? { tab: "calendar" } : {})}
        items={[
          {
            key: "daily",
            label: "Daily attendance",
            children: (
            <Card>
              <Space style={{ marginBottom: 16 }} wrap>
                <DatePicker
                  value={date}
                  onChange={(d) => {
                    const nd = d || moment();
                    setDate(nd);
                    fetchData(nd, status);
                  }}
                />
                <Select
                  allowClear
                  placeholder="Filter by status"
                  style={{ width: 200 }}
                  options={statusOptions}
                  value={status}
                  onChange={(v) => {
                    setStatus(v);
                    fetchData(date, v);
                  }}
                />
              </Space>
              <Table
                rowKey="_id"
                columns={columns}
                dataSource={sessions}
                pagination={{ pageSize: 15 }}
                locale={{ emptyText: <EmptyState message="No attendance sessions found" /> }}
              />
            </Card>
            ),
          },
          { key: "calendar", label: "Employee calendar", children: <EmployeeCalendarTab /> },
        ]}
      />
    </PageTransition>
  );
};

export default Attendance;
